import React from "react";
import ReactMarkdown from "react-markdown";

import { a } from "@react-spring/web";
import { useIntersect } from "hooks/useIntersect";
import { useAnimateInTrail } from "hooks/useAnimateInTrail";
import {
  initialOffset,
  animationTriggerThreshold,
} from "components/Animations/SpringProperties/SpringProperties";

import classNames from "classnames";
import * as fromProjects from "store/Projects";

import { ProjectBulletList } from "components/Project/ProjectTitleSections/ProjectBulletList";

interface IProps {
  animateIn: boolean;
  contentSection: fromProjects.IContentSection;
  titleContent: object;
  sticky: boolean;
  verticallyCentred: boolean;
}

export const Default: React.FC<IProps> = ({
  animateIn,
  contentSection,
  sticky,
  verticallyCentred,
  ...props
}) => {
  const titleContent: fromProjects.IContentTitleDefaultSection =
    props.titleContent as fromProjects.IContentTitleDefaultSection;

  const [inViewRef, inView] = useIntersect({
    startObserving: animateIn,
  });

  const [animateInTrail, triggerAnimateIn] = useAnimateInTrail(inView, 6, {
    notify: [
      {
        index: 5,
        threshold: animationTriggerThreshold,
      },
    ],
  });

  const titleStyles = {
    opacity: animateInTrail[0].active,
    transform: animateInTrail[0].active.to(
      (x) => `translateY(calc(${initialOffset(x)}))`
    ),
  };

  const getTitle = () => {
    if (titleContent.title != null)
      return (
        <a.h1 style={titleStyles}>
          <strong>{titleContent.title}</strong>
          {titleContent.subtitle}
        </a.h1>
      );
    else return null;
  };

  const bulletWrapperStyles = {
    transform: animateInTrail[2].active.to(
      (x) => `translateY(calc(${initialOffset(x)}))`
    ),
  };

  const getBulletPoints = () => {
    if (titleContent.bulletPoints != null)
      return (
        <a.div style={bulletWrapperStyles}>
          <ProjectBulletList
            bulletPoints={titleContent.bulletPoints}
            animationReady={triggerAnimateIn(5)}
          />
        </a.div>
      );
    else return null;
  };

  const descriptionStyles = {
    opacity: animateInTrail[1].active,
    transform: animateInTrail[1].active.to(
      (x) => `translateY(calc(${initialOffset(x)}))`
    ),
  };

  const titleBackground = {
    backgroundColor: contentSection.backgroundColor,
  };

  return (
    <div
      ref={inViewRef}
      className={classNames(
        "titleWrapper",
        "default",
        { mediumTopPadding: titleContent.topPadding === "medium" },
        { largeTopPadding: titleContent.topPadding === "large" },
        { alignRight: titleContent.align === "right" },
        { alignLeft: titleContent.align === "left" },
        { sticky },
        { verticallyCentred }
      )}
    >
      <div className="titleBackground" style={titleBackground} />
      {getTitle()}
      <a.div className="description" style={descriptionStyles}>
        <ReactMarkdown source={titleContent.description} />
      </a.div>
      {getBulletPoints()}
    </div>
  );
};
